import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CONFIG from "@/config/env";

export const useAuthFetch = () => {
  const navigate = useNavigate();
  const apiBase = CONFIG.API_BASE;

  const refreshSession = async () => {
    const refreshToken = localStorage.getItem("genofx:refreshToken");
    const username = localStorage.getItem("genofx:username");

    if (!refreshToken || !username) return null;

    try {
      // In development, use relative URLs to go through Vite proxy
      const isDev = import.meta.env.DEV;
      const refreshUrl = isDev ? '/api/auth/refresh' : `${apiBase}/api/auth/refresh`;

      const response = await fetch(refreshUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken, username, sessionId: localStorage.getItem("genofx:sessionId") }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("genofx:token", data.token);
        if (data.accessToken) {
          localStorage.setItem("genofx:accessToken", data.accessToken);
        }
        if (data.refreshToken) {
          localStorage.setItem("genofx:refreshToken", data.refreshToken);
        }
        if (data.user?.email || data.email) {
          localStorage.setItem("genofx:email", data.user?.email || data.email);
        }
        return data.token;
      }
    } catch (error) {
      console.error("Refresh error:", error);
    }
    return null;
  };

  return useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem("genofx:token");
    const accessToken = localStorage.getItem("genofx:accessToken");
    const sessionId = localStorage.getItem("genofx:sessionId");



    const headers = {
      ...options.headers,
      "Authorization": `Bearer ${token}`,
      "x-access-token": accessToken || "",
      "x-session-id": sessionId || "",
    };

    try {

      // In development, use relative URLs for /api/* routes to go through Vite proxy (avoids CORS)
      const isDev = import.meta.env.DEV;
      const finalUrl = url.startsWith('http')
        ? url
        : (isDev && url.startsWith('/api') ? url : `${apiBase}${url}`);


      let response = await fetch(finalUrl, {
        ...options,
        headers,
      });

      // Handle backend-down scenarios gracefully (502/503/504)
      // Return a mock Response with empty JSON to prevent crashes
      if (response.status === 502 || response.status === 503 || response.status === 504) {
        console.warn(`[useAuthFetch] Backend unavailable (${response.status}) for ${url}`);
        // Return a fake "ok" response with empty data so components don't crash
        return new Response(JSON.stringify({}), {
          status: response.status,
          statusText: response.statusText,
          headers: new Headers({ 'content-type': 'application/json' }),
        });
      }

      if (response.status === 401) {
        // Check if access token is actually expired before trying refresh
        const currentToken = localStorage.getItem("genofx:token");
        let isTokenExpired = true;
        if (currentToken) {
          try {
            const [, payloadB64] = currentToken.split('.');
            const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
            isTokenExpired = payload.exp ? (payload.exp * 1000) < Date.now() : false;
          } catch { isTokenExpired = true; }
        }

        if (!isTokenExpired) {
          // Token hasn't expired — the 401 might be a server-side session issue
          // Retry once with the same token (server may have restarted)
          return response;
        }

        // Token is expired — try refresh
        const newToken = await refreshSession();
        if (newToken) {
          const newAccessToken = localStorage.getItem("genofx:accessToken");
          const newHeaders = {
            ...options.headers,
            "Authorization": `Bearer ${newToken}`,
            "x-access-token": newAccessToken || "",
          };
          response = await fetch(finalUrl, {
            ...options,
            headers: newHeaders,
          });
          if (response.ok) return response;
        }

        // Both token expired AND refresh failed — truly expired
        localStorage.removeItem("genofx:token");
        localStorage.removeItem("genofx:accessToken");
        localStorage.removeItem("genofx:refreshToken");
        localStorage.removeItem("genofx:username");
        localStorage.removeItem("genofx:sessionId");
        toast.error("Your session has expired. Please log in again.");
        const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        navigate(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
        return response;
      }
      return response;
    } catch (error) {
      console.warn("[useAuthFetch] Network error for", url, error);
      // Return a fake response so components don't crash on network failure
      return new Response(JSON.stringify({}), {
        status: 0,
        statusText: 'Network Error',
        headers: new Headers({ 'content-type': 'application/json' }),
      });
    }
  }, [navigate, apiBase]);
};
